import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FLIP_KEY, TOTAL_PAGES, pageImageSrc } from '@/lib/book';
import { readLastPage, saveLastPage } from '@/hooks/useLastPage';
import ReaderToolbar from '@/pages/reader/components/ReaderToolbar';
import ReaderStage from '@/pages/reader/components/ReaderStage';
import ReaderFooterControls from '@/pages/reader/components/ReaderFooterControls';
import TocPanel from '@/pages/reader/components/TocPanel';
import {
  clampPage,
  nextPage as calcNext,
  prevPage as calcPrev,
  spreadPages,
  type ViewMode,
} from '@/pages/reader/readerUtils';

function readFlip(): boolean {
  try {
    const raw = window.localStorage.getItem(FLIP_KEY);
    return raw === null ? true : raw === '1';
  } catch {
    return true;
  }
}

export default function Reader() {
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(() => {
    const raw = searchParams.get('page');
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? 1 : clampPage(parsed);
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() => (window.innerWidth >= 900 ? 'spread' : 'single'));
  const [showToc, setShowToc] = useState(() => searchParams.get('toc') === '1');
  const [zoom, setZoom] = useState(1);
  const [flipEnabled, setFlipEnabled] = useState(readFlip);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);

  const goTo = useCallback((target: number, dir: 1 | -1 = 1) => {
    setDirection(dir);
    setPage(clampPage(target));
  }, []);

  const goNext = useCallback(() => {
    setPage((cur) => {
      setDirection(1);
      return calcNext(cur, viewMode);
    });
  }, [viewMode]);

  const goPrev = useCallback(() => {
    setPage((cur) => {
      setDirection(-1);
      return calcPrev(cur, viewMode);
    });
  }, [viewMode]);

  // 마지막으로 본 쪽 저장
  useEffect(() => {
    saveLastPage(page);
  }, [page]);

  // 앞뒤 3쪽 미리 불러오기 (지연 로딩)
  useEffect(() => {
    for (let i = page - 3; i <= page + 3; i += 1) {
      if (i >= 1 && i <= TOTAL_PAGES) {
        const img = new Image();
        img.src = pageImageSrc(i);
      }
    }
  }, [page]);

  // 키보드 조작
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') setShowToc(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  // 전체화면 상태 동기화
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFlip = () => {
    setFlipEnabled((v) => {
      const nextVal = !v;
      try {
        window.localStorage.setItem(FLIP_KEY, nextVal ? '1' : '0');
      } catch {
        /* ignore */
      }
      return nextVal;
    });
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      /* fullscreen denied */
    }
  };

  const pages = spreadPages(page, viewMode);
  const spreadKey = pages.join('-');
  const label = pages.length > 1 ? `${pages[0]}–${pages[1]} / ${TOTAL_PAGES}` : `${page} / ${TOTAL_PAGES}`;
  const canPrev = page > 1;
  const canNext = page < TOTAL_PAGES;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-primary-950">
      <ReaderToolbar
        page={page}
        viewMode={viewMode}
        zoom={zoom}
        flipEnabled={flipEnabled}
        isFullscreen={isFullscreen}
        onOpenToc={() => setShowToc(true)}
        onToggleView={() => setViewMode((m) => (m === 'spread' ? 'single' : 'spread'))}
        onZoomIn={() => setZoom((z) => Math.min(3, Number((z + 0.25).toFixed(2))))}
        onZoomOut={() => setZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))))}
        onResetZoom={() => setZoom(1)}
        onToggleFlip={toggleFlip}
        onToggleFullscreen={toggleFullscreen}
      />

      <ReaderStage
        pages={pages}
        zoom={zoom}
        flipEnabled={flipEnabled}
        direction={direction}
        spreadKey={spreadKey}
        canPrev={canPrev}
        canNext={canNext}
        onPrev={goPrev}
        onNext={goNext}
        onZoomChange={setZoom}
      />

      <ReaderFooterControls page={page} total={TOTAL_PAGES} label={label} onJump={(p) => goTo(p)} />

      <TocPanel
        open={showToc}
        currentPage={page}
        onClose={() => setShowToc(false)}
        onSelect={(p) => {
          setShowToc(false);
          goTo(p);
        }}
      />
    </div>
  );
}